/**
 * @module gorgias-chat-open
 * @description Delegates `[data-open-gorgias-chat]` clicks to Gorgias and waits for
 *   `gorgias-widget-loaded` when the widget script is still loading (matches legacy deferred init).
 */

const LOADED_EVENT = 'gorgias-widget-loaded';

/**
 * @returns {boolean} True if open was invoked
 */
function tryOpen() {
  const chat = window.GorgiasChat;
  if (!chat || typeof chat.open !== 'function') return false;

  // A widget set to manual start ignores open() until init() has resolved;
  // init() on an already-running widget is a no-op, so always go through it.
  const ready = typeof chat.init === 'function' ? Promise.resolve(chat.init()) : Promise.resolve();
  ready.catch(() => {}).then(() => chat.open());
  return true;
}

/**
 * Opens Gorgias chat, or subscribes once to the loaded event.
 */
export function openGorgiasChat() {
  if (tryOpen()) return;
  window.addEventListener(LOADED_EVENT, () => {
    tryOpen();
  }, { once: true });
}

/**
 * Unhides `[data-requires-gorgias]` triggers (rendered `hidden` in Liquid) once
 * the widget exists, so a missing, blocked or failed Gorgias load never leaves
 * a button that does nothing.
 */
function revealGorgiasTriggers() {
  const chat = window.GorgiasChat;
  if (!chat || typeof chat.open !== 'function') return;
  document.querySelectorAll('[data-requires-gorgias][hidden]').forEach((el) => {
    el.hidden = false;
  });
}

function initGorgiasTriggers() {
  revealGorgiasTriggers();
  window.addEventListener(LOADED_EVENT, revealGorgiasTriggers);
  // A morphed-in-place section (e.g. combined-listing swap) re-renders its
  // triggers hidden again.
  document.addEventListener('theme:section:morph', revealGorgiasTriggers);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGorgiasTriggers);
} else {
  initGorgiasTriggers();
}

document.addEventListener(
  'click',
  (e) => {
    const el = e.target.closest('[data-open-gorgias-chat]');
    if (!el) return;
    e.preventDefault();
    openGorgiasChat();
  },
  true
);
